import React, { useState, useEffect } from 'react'
import { History, Undo, Download, RefreshCw } from 'lucide-react'
import { AssociationChange } from '../types'
import { fundersService } from '../services/fundersService'

interface ChangesTabProps {
  onDataChanged?: () => void
}

export const ChangesTab: React.FC<ChangesTabProps> = ({ onDataChanged }) => {
  const [changes, setChanges] = useState<AssociationChange[]>([])
  const [loading, setLoading] = useState(true)
  const [undoingId, setUndoingId] = useState<string | null>(null)

  useEffect(() => {
    loadChanges()
  }, [])

  const loadChanges = async () => {
    try {
      setLoading(true)
      const changesData = await fundersService.getChanges()
      setChanges(changesData)
    } catch (error) {
      console.error('Error loading changes:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUndo = async (changeId: string) => {
    try {
      setUndoingId(changeId)
      await fundersService.undoChange(changeId)
      await loadChanges()
      onDataChanged?.()
    } catch (error) {
      console.error('Error undoing change:', error)
      alert('Failed to undo change')
    } finally {
      setUndoingId(null)
    }
  }

  const handleExport = async () => {
    try {
      const csvContent = await fundersService.exportChanges()
      
      if (csvContent === 'No changes to export') {
        alert('No changes to export')
        return
      }

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `funders-changes-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exporting changes:', error)
      alert('Failed to export changes')
    }
  }

  const formatDateTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getAssociationColor = (label: string) => {
    switch (label.toLowerCase()) {
      case 'funder':
        return { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' }
      case 'supplier':
        return { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' }
      case 'primary':
        return { bg: '#fef3c7', text: '#d97706', border: '#fde68a' }
      default:
        return { bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' }
    }
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        height: '200px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            animation: 'spin 1s linear infinite', 
            borderRadius: '50%', 
            height: '32px', 
            width: '32px', 
            border: '2px solid #3b82f6', 
            borderBottomColor: 'transparent',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
            Loading changes...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <History style={{ height: '20px', width: '20px', color: '#6b7280' }} />
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#111827',
              fontFamily: 'Lexend Deca, sans-serif',
              margin: 0
            }}>
              Association Changes
            </h2>
            <span style={{ 
              fontSize: '14px', 
              color: '#6b7280',
              fontFamily: 'Lexend Deca, sans-serif'
            }}>
              ({changes.length} changes)
            </span>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={loadChanges}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 16px',
                background: 'white',
                color: '#374151',
                fontWeight: '500',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <RefreshCw style={{ height: '16px', width: '16px', marginRight: '8px' }} />
              Refresh
            </button>
            <button
              onClick={handleExport}
              disabled={changes.length === 0}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 16px',
                background: changes.length > 0 ? '#2563eb' : '#e5e7eb',
                color: changes.length > 0 ? 'white' : '#9ca3af',
                fontWeight: '500',
                borderRadius: '8px',
                border: 'none',
                cursor: changes.length > 0 ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (changes.length > 0) {
                  e.currentTarget.style.background = '#1d4ed8'
                }
              }}
              onMouseLeave={(e) => {
                if (changes.length > 0) {
                  e.currentTarget.style.background = '#2563eb'
                }
              }}
            >
              <Download style={{ height: '16px', width: '16px', marginRight: '8px' }} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Changes List */}
        {changes.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '64px',
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb'
          }}>
            <History style={{ 
              height: '48px', 
              width: '48px', 
              color: '#d1d5db', 
              margin: '0 auto 16px' 
            }} />
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '500', 
              color: '#6b7280',
              fontFamily: 'Lexend Deca, sans-serif',
              marginBottom: '8px'
            }}>
              No changes yet
            </h3>
            <p style={{ 
              color: '#9ca3af',
              fontFamily: 'Lexend Deca, sans-serif'
            }}>
              Association changes will appear here when you edit company associations
            </p>
          </div>
        ) : (
          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            overflow: 'hidden',
            border: '1px solid #e5e7eb'
          }}>
            {changes.map((change, index) => (
              <div key={change.id} style={{
                padding: '20px',
                borderBottom: index < changes.length - 1 ? '1px solid #f3f4f6' : 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <span style={{ 
                        fontSize: '14px', 
                        color: '#6b7280',
                        fontFamily: 'Lexend Deca, sans-serif'
                      }}>
                        {formatDateTime(change.timestamp)}
                      </span>
                      <span style={{ 
                        fontSize: '14px', 
                        color: '#9ca3af',
                        fontFamily: 'Lexend Deca, sans-serif'
                      }}>
                        by {change.user}
                      </span>
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <h4 style={{ 
                        fontSize: '16px', 
                        fontWeight: '500', 
                        color: '#111827',
                        fontFamily: 'Lexend Deca, sans-serif',
                        marginBottom: '4px'
                      }}>
                        {change.deal_name}
                      </h4>
                      <div style={{ 
                        fontSize: '14px', 
                        color: '#6b7280',
                        fontFamily: 'Lexend Deca, sans-serif'
                      }}>
                        <span style={{ fontWeight: '500', color: '#111827' }}>
                          {change.company_name}
                        </span>
                        {change.company_domain && (
                          <span style={{ marginLeft: '8px' }}>
                            {change.company_domain}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ 
                        fontSize: '14px', 
                        color: '#6b7280',
                        fontFamily: 'Lexend Deca, sans-serif'
                      }}>
                        Changed from
                      </span>
                      {(() => {
                        const oldColors = getAssociationColor(change.old_association)
                        return (
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background: oldColors.bg,
                            color: oldColors.text,
                            border: `1px solid ${oldColors.border}`
                          }}>
                            {change.old_association}
                          </span>
                        )
                      })()}
                      <span style={{ 
                        fontSize: '14px', 
                        color: '#6b7280',
                        fontFamily: 'Lexend Deca, sans-serif'
                      }}>
                        to
                      </span>
                      {(() => {
                        const newColors = getAssociationColor(change.new_association)
                        return (
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background: newColors.bg,
                            color: newColors.text,
                            border: `1px solid ${newColors.border}`
                          }}>
                            {change.new_association}
                          </span>
                        )
                      })()}
                    </div>
                  </div>

                  <button
                    onClick={() => handleUndo(change.id)}
                    disabled={undoingId === change.id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      background: 'white',
                      color: '#dc2626',
                      fontWeight: '500',
                      borderRadius: '8px',
                      border: '1px solid #fca5a5',
                      cursor: undoingId === change.id ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontFamily: 'Lexend Deca, sans-serif',
                      transition: 'all 0.2s',
                      opacity: undoingId === change.id ? 0.5 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (undoingId !== change.id) {
                        e.currentTarget.style.background = '#fef2f2'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (undoingId !== change.id) {
                        e.currentTarget.style.background = 'white'
                      }
                    }}
                  >
                    <Undo style={{ height: '16px', width: '16px', marginRight: '6px' }} />
                    {undoingId === change.id ? 'Undoing...' : 'Undo'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}